import { useEffect, useRef, useState } from 'react'
import { useForm, Link } from '@inertiajs/react'
import { Loader2, Play, Square } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const TYPE_LABELS = {
  text: 'Text', long_text: 'Long text', date: 'Date', number: 'Number',
  boolean: 'Yes / no', weight_lb: 'Weight (lb)', height_ft_in: 'Height (ft/in)', enum: 'Choice',
}

export default function AgentForm({
  agent, organization, defaults, avatars = [], voices = [], languages = [],
  fieldTypes = [], availableModels = [], modelProviders = {},
  currentUser, errors: serverErrors, flash,
}) {
  const isNew = !agent?.id
  const publicTriageUrl = !isNew && typeof window !== 'undefined'
    ? `${window.location.origin}${agent.public_url}` : (agent.public_url || '')

  const { data, setData, post, patch, processing, errors } = useForm({
    agent: {
      name: agent.name || '',
      slug: agent.slug || '',
      default_model: agent.default_model || (availableModels[0] || 'gemini-live'),
      language: agent.language || defaults.language || 'Any',
      time_limit_seconds: agent.time_limit_seconds || defaults.time_limit_seconds || 300,
      description: agent.description || '',
      first_message: agent.first_message || '',
      system_prompt: agent.system_prompt || '',
      avatar_id: agent.avatar_id || '',
      voice_id: agent.voice_id || '',
      public_access: Boolean(agent.public_access),
      intake_schema: agent.intake_schema || defaults.intake_schema,
    },
  })

  const isPatientSim = agent.kind === 'patient_sim'

  const allErrors = { ...errors, ...serverErrors }
  const promptInUse = data.agent.system_prompt.trim().length > 0
  const avatarInUse = data.agent.avatar_id.trim().length > 0

  // Each pipeline speaks through one voice provider; only offer voices that
  // exist for the currently selected model's provider.
  const voiceProvider = modelProviders[data.agent.default_model]
  const availableVoices = voiceProvider ? voices.filter((v) => v[voiceProvider]) : voices

  const audioRef = useRef(null)
  const previewTokenRef = useRef(0)
  const [preview, setPreview] = useState({ voiceId: null, status: 'idle' })
  const [previewError, setPreviewError] = useState(null)

  function stopPreview() {
    previewTokenRef.current += 1
    if (audioRef.current) {
      audioRef.current.pause()
      if (audioRef.current.dataset?.objectUrl) URL.revokeObjectURL(audioRef.current.dataset.objectUrl)
      audioRef.current = null
    }
    setPreview({ voiceId: null, status: 'idle' })
  }

  useEffect(() => stopPreview, [])

  async function togglePreview(voice) {
    setPreviewError(null)
    if (preview.voiceId === voice.id) { stopPreview(); return }
    stopPreview()
    const token = previewTokenRef.current
    setPreview({ voiceId: voice.id, status: 'loading' })
    try {
      const res = await fetch(`/admin/voice_samples/${voiceProvider}/${voice.id}`)
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        throw new Error(body?.error || `Sample unavailable (HTTP ${res.status})`)
      }
      const blob = await res.blob()
      if (previewTokenRef.current !== token) return
      const url = URL.createObjectURL(blob)
      const audio = new Audio(url)
      audio.dataset.objectUrl = url
      audioRef.current = audio
      audio.addEventListener('ended', () => {
        if (audioRef.current === audio) stopPreview()
      })
      await audio.play()
      if (previewTokenRef.current !== token) return
      setPreview({ voiceId: voice.id, status: 'playing' })
    } catch (err) {
      if (previewTokenRef.current !== token) return
      setPreviewError(err.message)
      stopPreview()
    }
  }

  function handleModelChange(model) {
    const provider = modelProviders[model]
    const voiceStillAvailable = !data.agent.voice_id
      || !provider
      || voices.some((v) => v.id === data.agent.voice_id && v[provider])
    setData('agent', { ...data.agent, default_model: model, voice_id: voiceStillAvailable ? data.agent.voice_id : '' })
    stopPreview()
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (isNew) post('/admin/agents')
    else patch(`/admin/agents/${agent.id}`)
  }

  function updateSchemaField(index, patchObj) {
    const next = [...data.agent.intake_schema]
    next[index] = { ...next[index], ...patchObj }
    setData('agent', { ...data.agent, intake_schema: next })
  }
  function addSchemaField() {
    setData('agent', { ...data.agent, intake_schema: [...data.agent.intake_schema, { key: '', label: '', type: 'text', section: 'Custom' }] })
  }
  function removeSchemaField(index) {
    setData('agent', { ...data.agent, intake_schema: data.agent.intake_schema.filter((_, i) => i !== index) })
  }
  function moveSchemaField(index, delta) {
    const next = [...data.agent.intake_schema]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    setData('agent', { ...data.agent, intake_schema: next })
  }
  function resetSchemaToDefault() {
    setData('agent', { ...data.agent, intake_schema: defaults.intake_schema })
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <h1 className="text-xl font-bold">Live Triage</h1>
          <div className="flex items-center gap-4">
            <Link href="/admin/agents"><Button variant="ghost" size="sm">All agents</Button></Link>
            <Link href="/dashboard"><Button variant="ghost" size="sm">Dashboard</Button></Link>
            <span className="text-sm text-muted-foreground">{currentUser?.email}</span>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-3xl px-4 py-10">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold tracking-tight">{isNew ? 'New agent' : `Edit ${agent.name}`}</h2>
        </div>

        {flash?.notice && (
          <div className="mb-4 rounded-md border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700">{flash.notice}</div>
        )}

        {!isNew && publicTriageUrl && (
          <div className="mb-6 rounded-md border bg-muted/30 px-4 py-3 text-sm">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Public triage URL</div>
            <a href={publicTriageUrl} target="_blank" rel="noreferrer" className="font-mono text-sm underline-offset-4 hover:underline">{publicTriageUrl}</a>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={data.agent.name} onChange={(e) => setData('agent', { ...data.agent, name: e.target.value })} />
              {allErrors?.name && <p className="text-sm text-destructive">{allErrors.name.join(', ')}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">URL slug</Label>
              <Input id="slug" value={data.agent.slug} onChange={(e) => setData('agent', { ...data.agent, slug: e.target.value })} placeholder="auto-generated" className="font-mono text-sm" />
              {allErrors?.slug && <p className="text-sm text-destructive">{allErrors.slug.join(', ')}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="default_model">Default model</Label>
              <select
                id="default_model"
                value={data.agent.default_model}
                onChange={(e) => handleModelChange(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {availableModels.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="language">Language</Label>
              <select
                id="language"
                value={data.agent.language}
                onChange={(e) => setData('agent', { ...data.agent, language: e.target.value })}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {languages.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <p className="text-xs text-muted-foreground">{data.agent.language === 'Any' ? 'Mirrors whatever language the caller speaks.' : `Bot always speaks ${data.agent.language}.`}</p>
              {allErrors?.language && <p className="text-sm text-destructive">{allErrors.language.join(', ')}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="time_limit_minutes">Time limit (minutes)</Label>
            <Input
              id="time_limit_minutes"
              type="number"
              min="1"
              className="w-40"
              value={Math.round((data.agent.time_limit_seconds || 0) / 60) || ''}
              onChange={(e) => {
                const mins = parseInt(e.target.value, 10)
                setData('agent', { ...data.agent, time_limit_seconds: Number.isFinite(mins) && mins > 0 ? mins * 60 : '' })
              }}
            />
            <p className="text-xs text-muted-foreground">Maximum call length before the session ends. Currently {data.agent.time_limit_seconds || 0}s.</p>
            {allErrors?.time_limit_seconds && <p className="text-sm text-destructive">{allErrors.time_limit_seconds.join(', ')}</p>}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Voice</Label>
              <span className="text-xs text-muted-foreground">{data.agent.voice_id ? `Selected: ${data.agent.voice_id}` : 'Using default voice for the avatar'}</span>
            </div>
            {voiceProvider && (
              <p className="text-xs text-muted-foreground">Showing voices available for <span className="font-mono">{data.agent.default_model}</span>.</p>
            )}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <button type="button" onClick={() => setData('agent', { ...data.agent, voice_id: '' })}
                className={`rounded-md border px-3 py-2 text-left text-sm transition ${!data.agent.voice_id ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/50'}`}>
                <div className="font-medium">Avatar default</div>
                <div className="text-xs text-muted-foreground">Match the avatar's gender</div>
              </button>
              {availableVoices.map((v) => {
                const selected = data.agent.voice_id === v.id
                const isPreviewing = preview.voiceId === v.id
                return (
                  <div
                    key={v.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setData('agent', { ...data.agent, voice_id: v.id })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setData('agent', { ...data.agent, voice_id: v.id })
                      }
                    }}
                    className={`flex cursor-pointer items-start justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition ${selected ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/50'}`}
                  >
                    <div>
                      <div className="font-medium">{v.name} <span className="text-xs font-normal text-muted-foreground">· {v.gender}</span></div>
                      {v.description && <div className="text-xs text-muted-foreground">{v.description}</div>}
                    </div>
                    <button
                      type="button"
                      title={isPreviewing ? 'Stop sample' : `Play a sample of ${v.name}`}
                      aria-label={isPreviewing ? 'Stop sample' : `Play a sample of ${v.name}`}
                      onClick={(e) => { e.stopPropagation(); togglePreview(v) }}
                      className="mt-0.5 shrink-0 rounded-full border p-1.5 text-muted-foreground transition hover:border-primary hover:text-primary"
                    >
                      {isPreviewing && preview.status === 'loading'
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : isPreviewing
                          ? <Square className="h-3.5 w-3.5" />
                          : <Play className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                )
              })}
            </div>
            {previewError && <p className="text-sm text-destructive">{previewError}</p>}
            {allErrors?.voice_id && <p className="text-sm text-destructive">{allErrors.voice_id.join(', ')}</p>}
          </div>

          {!isNew && (
            <div className="space-y-2">
              <Label>Access</Label>
              <div className="rounded-md border bg-background p-3 text-sm">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={isPatientSim ? false : data.agent.public_access}
                    disabled={isPatientSim}
                    onChange={(e) => setData('agent', { ...data.agent, public_access: e.target.checked })}
                  />
                  <span>
                    <span className="font-medium">Allow public access</span>
                    <span className="block text-xs text-muted-foreground">
                      {isPatientSim
                        ? "Training cases always require sign-in — visitors can't run a patient sim anonymously."
                        : "When off, visitors must sign in (with a verified work email) before they can start a call."}
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}

          {(
            <>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input id="description" value={data.agent.description} onChange={(e) => setData('agent', { ...data.agent, description: e.target.value })} placeholder="One-line tagline shown on the landing page card" />
                <p className="text-xs text-muted-foreground">Short tagline shown on the public homepage card.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="first_message">First message</Label>
                <Input id="first_message" value={data.agent.first_message} onChange={(e) => setData('agent', { ...data.agent, first_message: e.target.value })} placeholder="Leave blank to let the system prompt decide" />
                <p className="text-xs text-muted-foreground">Bot's first spoken line. Leave blank to use whatever the system prompt produces.</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="system_prompt">System prompt</Label>
                  <span className="text-xs text-muted-foreground">{promptInUse ? 'Custom' : 'Using default Mount Sinai prompt'}</span>
                </div>
                <textarea
                  id="system_prompt"
                  className="min-h-[260px] w-full rounded-md border bg-background px-3 py-2 font-mono text-xs"
                  value={data.agent.system_prompt}
                  onChange={(e) => setData('agent', { ...data.agent, system_prompt: e.target.value })}
                  placeholder={defaults.system_prompt}
                />
                <div className="flex justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setData('agent', { ...data.agent, system_prompt: defaults.system_prompt })}>
                    Load default into editor
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Avatar</Label>
                  <span className="text-xs text-muted-foreground">{avatarInUse ? `Selected: ${data.agent.avatar_id.slice(0, 8)}…` : 'Using default doctor avatar'}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {avatars.map((avatar) => {
                    const selected = data.agent.avatar_id === avatar.id || (!data.agent.avatar_id && avatar.id === defaults.avatar_id)
                    return (
                      <button key={avatar.id} type="button" onClick={() => setData('agent', { ...data.agent, avatar_id: avatar.id })}
                        className={`group relative overflow-hidden rounded-md border bg-card text-left transition ${selected ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/50'}`}>
                        <img src={avatar.preview_url} alt={avatar.name} loading="lazy" className="aspect-[3/4] w-full object-cover" />
                        <div className="px-2 py-1.5">
                          <div className="text-xs font-medium leading-tight">{avatar.name}</div>
                          {avatar.id === defaults.avatar_id && <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Default</div>}
                        </div>
                      </button>
                    )
                  })}
                </div>
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer select-none">Use a custom avatar ID</summary>
                  <div className="mt-2 space-y-1">
                    <Input value={data.agent.avatar_id} onChange={(e) => setData('agent', { ...data.agent, avatar_id: e.target.value })} placeholder={defaults.avatar_id} spellCheck={false} />
                  </div>
                </details>
              </div>

              <div className="space-y-3">
                <Label>Intake fields</Label>
                <p className="text-xs text-muted-foreground">Each row is one intake field. The key is used in the bot's tool calls; the label shows on screen.</p>
                <div className="overflow-hidden rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="border-b bg-muted/40">
                      <tr className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        <th className="w-32 px-3 py-2">Key</th>
                        <th className="px-3 py-2">Label</th>
                        <th className="w-32 px-3 py-2">Type</th>
                        <th className="w-28 px-3 py-2">Section</th>
                        <th className="w-24 px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {data.agent.intake_schema.map((field, index) => (
                        <tr key={index} className="text-sm">
                          <td className="px-3 py-2"><Input value={field.key || ''} onChange={(e) => updateSchemaField(index, { key: e.target.value })} placeholder="key_name" className="font-mono text-xs" /></td>
                          <td className="px-3 py-2"><Input value={field.label || ''} onChange={(e) => updateSchemaField(index, { label: e.target.value })} placeholder="Label" /></td>
                          <td className="px-3 py-2">
                            <select value={field.type || 'text'} onChange={(e) => updateSchemaField(index, { type: e.target.value })} className="w-full rounded-md border bg-background px-2 py-1.5 text-sm">
                              {fieldTypes.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-2"><Input value={field.section || ''} onChange={(e) => updateSchemaField(index, { section: e.target.value })} placeholder="Section" /></td>
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-end gap-1">
                              <button type="button" onClick={() => moveSchemaField(index, -1)} disabled={index === 0} className="rounded px-1.5 text-xs text-muted-foreground hover:bg-muted disabled:opacity-30">↑</button>
                              <button type="button" onClick={() => moveSchemaField(index, 1)} disabled={index === data.agent.intake_schema.length - 1} className="rounded px-1.5 text-xs text-muted-foreground hover:bg-muted disabled:opacity-30">↓</button>
                              <button type="button" onClick={() => removeSchemaField(index)} className="rounded px-1.5 text-xs text-destructive hover:bg-destructive/10">✕</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between">
                  <Button type="button" variant="outline" size="sm" onClick={addSchemaField}>+ Add field</Button>
                  <Button type="button" variant="ghost" size="sm" onClick={resetSchemaToDefault}>Reset to default schema</Button>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2">
            <Link href="/admin/agents"><Button type="button" variant="outline">Cancel</Button></Link>
            <Button type="submit" disabled={processing}>{isNew ? 'Create' : 'Save'}</Button>
          </div>
        </form>
      </main>
    </div>
  )
}
